/**
 * React Query hooks for image data fetching
 * Wraps existing image API with React Query for automatic caching, refetching, and state management
 */

import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchUserImages,
  deleteImage,
  type ImageListResponse
} from '@/api/imageApi';
import imageKeys from '@/api/querry_keys/imageKeys';

/**
 * Hook for fetching user images with infinite scroll pagination
 * Automatically caches and manages image list state
 */
interface UseUserImagesInfiniteParams {
  userEmail: string | null;
  limit?: number;
  status?: string;
  sort_by?: string;
  mode?: string;
  enabled?: boolean;
}

export const useUserImagesInfinite = (params: UseUserImagesInfiniteParams) => {
  return useInfiniteQuery({
    queryKey: imageKeys.infinite(params as any),
    queryFn: async ({ pageParam = 0 }) => {
      const response = await fetchUserImages(
        params.limit || 12,
        pageParam,
        params.mode,
        params.status,
        params.sort_by
      );
      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: ImageListResponse) => {
      const nextOffset = (lastPage.offset || 0) + (lastPage.limit || 12);
      return lastPage.has_more ? nextOffset : undefined;
    },
    enabled: !!params.userEmail && (params.enabled ?? true), // Only fetch when userEmail is available and gallery is enabled
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Helper function to filter images from a page
 */
const filterImagesFromPage = (page: any, imageId: string) => ({
  ...page,
  images: page.images.filter((img: any) => img.id !== imageId)
});

/**
 * Helper function to remove image from query cache
 */
const removeImageFromCache = (old: any, imageId: string) => {
  if (!old) return old;

  // Handle infinite query structure
  if (old.pages) {
    return {
      ...old,
      pages: old.pages.map((page: any) => filterImagesFromPage(page, imageId))
    };
  }

  // Handle array structure
  if (Array.isArray(old)) {
    return old.filter((img: any) => img.id !== imageId);
  }

  return old;
};

/**
 * Hook for deleting an image with optimistic updates
 */
export const useDeleteImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (imageId: string) => {
      return await deleteImage(imageId);
    },
    // Optimistically remove image from cache immediately
    onMutate: async (imageId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: imageKeys.all });

      // Snapshot the previous value for rollback
      const previousData = queryClient.getQueriesData({ queryKey: imageKeys.all });

      // Optimistically remove the image from all image queries
      queryClient.setQueriesData(
        { queryKey: imageKeys.all },
        (old) => removeImageFromCache(old, imageId)
      );

      // Return context for rollback
      return { previousData };
    },
    // Rollback on error
    onError: (_err, _imageId, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    // Always refetch after success or error to ensure consistency
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: imageKeys.all });
    },
  });
};

/**
 * Helper hook that provides all image data with a compatible interface
 * This maintains backward compatibility with existing component structure
 */
export const useUserImagesWithMutations = (params: UseUserImagesInfiniteParams) => {
  const imagesQuery = useUserImagesInfinite(params);
  const deleteMutation = useDeleteImage();

  // Flatten pages into single array
  const images = imagesQuery.data?.pages.flatMap(page => page.images) ?? [];
  
  // Get total counts from the first page
  const totalCount = imagesQuery.data?.pages[0]?.total_count ?? 0;
  const totalUnfilteredCount = imagesQuery.data?.pages[0]?.total_unfiltered_count ?? 0;

  return {
    // Image data
    images,
    
    // Loading states
    isLoading: imagesQuery.isLoading,
    isLoadingMore: imagesQuery.isFetchingNextPage,
    
    // Error states
    error: imagesQuery.error,
    
    // Pagination
    hasMore: imagesQuery.hasNextPage ?? false,
    loadMore: imagesQuery.fetchNextPage,
    
    // Counts
    totalCount,
    totalUnfilteredCount,
    
    // Refetch functions
    refetch: async () => {
      await imagesQuery.refetch();
    },
    
    // Mutations
    deleteImage: (imageId: string) => 
      deleteMutation.mutate(imageId),
      
    // Mutation states
    isDeleting: deleteMutation.isPending,
    
    // Raw query objects for advanced use cases
    imagesQuery,
    
    // Setter functions for compatibility (these don't do anything in React Query)
    setImages: () => {
    },
  };
};
